import React, { useState } from 'react';

/**
 * 7-day stacked bar — each day shows Protein/Carbs/Fat as kcal contributions,
 * stacked vertically. Hover reveals exact gram counts.
 */
const COLOR_P = '#f97316';
const COLOR_C = '#3b82f6';
const COLOR_F = '#a855f7';

const WeekStackedChart = ({ days, target }) => {
  const [hover, setHover] = useState(null);
  const max = Math.max(target || 0, ...days.map(d => d.kcal), 1);

  return (
    <div className="relative">
      <div className="grid grid-cols-7 gap-2 items-end h-40" data-testid="week-chart">
        {days.map((d, idx) => {
          const kcalP = (d.protein_g || 0) * 4;
          const kcalC = (d.carb_g || 0) * 4;
          const kcalF = (d.fat_g || 0) * 9;
          const totalKcal = Math.max(d.kcal || 0, kcalP + kcalC + kcalF) || 0;
          const heightPct = (totalKcal / max) * 100;
          const day = new Date(d.date).toLocaleDateString([], { weekday: 'short' });
          const isToday = idx === days.length - 1;

          // Each segment height as fraction of the bar
          const sumK = (kcalP + kcalC + kcalF) || 1;
          const pSeg = (kcalP / sumK) * 100;
          const cSeg = (kcalC / sumK) * 100;
          const fSeg = (kcalF / sumK) * 100;

          return (
            <div key={d.date}
                 className="flex flex-col items-center gap-1 h-full cursor-pointer group"
                 data-testid={`week-day-${d.date}`}
                 onMouseEnter={() => setHover(d.date)}
                 onMouseLeave={() => setHover(h => (h === d.date ? null : h))}>
              <div className="flex-1 w-full flex items-end relative">
                {target && (
                  <div className="absolute left-0 right-0 border-t border-dashed border-primary/40"
                       style={{ bottom: `${(target / max) * 100}%` }} />
                )}
                <div className="w-full rounded-t overflow-hidden flex flex-col-reverse transition-all duration-500 group-hover:brightness-125"
                     style={{ height: totalKcal > 0 ? `${heightPct}%` : '4px', minHeight: '4px',
                              boxShadow: isToday ? `0 0 12px ${COLOR_C}55` : undefined }}>
                  {totalKcal > 0 ? (
                    <>
                      <div style={{ height: `${pSeg}%`, background: COLOR_P }} />
                      <div style={{ height: `${cSeg}%`, background: COLOR_C }} />
                      <div style={{ height: `${fSeg}%`, background: COLOR_F }} />
                    </>
                  ) : (
                    <div className="w-full bg-zinc-800" style={{ height: '100%' }} />
                  )}
                </div>
              </div>
              <span className={`text-xs ${isToday ? 'text-primary font-bold' : 'text-zinc-500'}`}>{day}</span>
              <span className={`text-xs ${isToday ? 'text-white' : 'text-zinc-400'}`}>{Math.round(totalKcal)}</span>

              {hover === d.date && totalKcal > 0 && (
                <div className="absolute bottom-full mb-2 bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-xs space-y-0.5 z-10 shadow-xl pointer-events-none whitespace-nowrap"
                     style={{ left: `${(idx / 6) * 100}%`, transform: 'translateX(-50%)' }}>
                  <div className="text-white font-semibold">{Math.round(totalKcal)} kcal</div>
                  <div style={{ color: COLOR_P }}>P {Math.round(d.protein_g || 0)}g</div>
                  <div style={{ color: COLOR_C }}>C {Math.round(d.carb_g || 0)}g</div>
                  <div style={{ color: COLOR_F }}>F {Math.round(d.fat_g || 0)}g</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 mt-3 text-xs">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLOR_P }} /><span className="text-zinc-400">Protein</span></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLOR_C }} /><span className="text-zinc-400">Carbs</span></span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: COLOR_F }} /><span className="text-zinc-400">Fat</span></span>
        {target && (
          <span className="flex items-center gap-1.5"><span className="w-3 border-t border-dashed border-primary" /><span className="text-zinc-400">Target {target} kcal</span></span>
        )}
      </div>
    </div>
  );
};

export default WeekStackedChart;
